import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import Loading from "../../../Shared/Loading";

const DoctorDashboardIndex = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch("https://servercareseebd.careseebd.com/api/v1/user/doctor-stats", {
      headers: {
        authorization: `Bearer ${localStorage.getItem("careSeeAccessToken")}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to load dashboard metrics");
        }
        return res.json();
      })
      .then((data) => {
        setStats(data?.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md my-5">
        <div className="flex items-center">
          <Icon icon="lucide:alert-circle" className="text-red-500 text-2xl mr-2" />
          <p className="text-red-700 font-semibold">Error: {error}</p>
        </div>
      </div>
    );
  }

  const summary = stats?.summary || {
    totalPatients: 0,
    activePatients: 0,
    inactivePatients: 0,
    activePackages: 0,
    expiredPackages: 0,
    totalGameSessions: 0,
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* 1. KPI Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Patients Card */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition duration-300 flex items-center gap-5 group">
          <div className="p-4 bg-indigo-50 text-secondary rounded-2xl group-hover:scale-110 duration-300">
            <Icon icon="solar:users-group-rounded-bold-duotone" className="text-3xl" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-400 uppercase tracking-wider">My Patients</p>
            <h3 className="text-3xl font-bold text-slate-800 mt-1">{summary.totalPatients}</h3>
            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
              {summary.activePatients} Active profiles
            </p>
          </div>
        </div>

        {/* Active Packages Card */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition duration-300 flex items-center gap-5 group">
          <div className="p-4 bg-cyan-50 text-primary rounded-2xl group-hover:scale-110 duration-300">
            <Icon icon="solar:box-minimalistic-bold-duotone" className="text-3xl" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-400 uppercase tracking-wider">Active Packages</p>
            <h3 className="text-3xl font-bold text-slate-800 mt-1">{summary.activePackages}</h3>
            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
              {summary.expiredPackages} Expired plans
            </p>
          </div>
        </div>

        {/* Therapy Session count */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition duration-300 flex items-center gap-5 group">
          <div className="p-4 bg-emerald-50 text-emerald-600 rounded-2xl group-hover:scale-110 duration-300">
            <Icon icon="solar:gamepad-bold-duotone" className="text-3xl" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-400 uppercase tracking-wider">Therapy sessions</p>
            <h3 className="text-3xl font-bold text-slate-800 mt-1">{summary.totalGameSessions}</h3>
            <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1 font-medium">
              <Icon icon="solar:graph-up-linear" />
              Completed by patients
            </p>
          </div>
        </div>
      </div>

      {/* 2. Actions and Quick link panel */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between p-6 bg-[#1e1b4b] text-white rounded-2xl border border-indigo-950 shadow-md">
        <div>
          <h4 className="text-lg font-bold">Need to register a new patient?</h4>
          <p className="text-xs text-slate-300 mt-0.5">Quickly provision user credentials and assign eye exercise therapy plans.</p>
        </div>
        <Link to="/doctorDashboard/doctor-add-new-patient">
          <button className="bg-[#01AEEF] hover:bg-[#01AEEF]/90 text-white font-bold py-2.5 px-6 rounded-xl flex items-center gap-2 hover:shadow-lg transition duration-200">
            <Icon icon="solar:user-plus-bold" className="text-lg" />
            <span>Add New Patient</span>
          </button>
        </Link>
      </div>

      {/* 3. Feeds/Lists Grid Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Registrations Table */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex justify-between items-center mb-5">
              <div>
                <h4 className="text-base font-bold text-slate-800">Recent Patient Enrollments</h4>
                <p className="text-xs text-slate-400 mt-0.5">Last 5 registered patient accounts</p>
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 bg-indigo-50 text-secondary rounded-full">New Profiles</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-100 text-slate-400 text-xs font-semibold uppercase">
                    <th className="pb-3">Name</th>
                    <th className="pb-3">Registered At</th>
                    <th className="pb-3 text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {stats?.recentPatients?.length > 0 ? (
                    stats.recentPatients.slice(0, 5).map((patient) => (
                      <tr key={patient._id} className="hover:bg-slate-50/50 transition">
                        <td className="py-3">
                          <div className="font-semibold text-slate-800">{patient.name}</div>
                          <div className="text-xs text-slate-450">{patient.email}</div>
                        </td>
                        <td className="py-3 text-slate-500">
                          {moment(patient.createdAt).format("DD MMM YYYY")}
                        </td>
                        <td className="py-3 text-right">
                          <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${patient.isActive ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                            {patient.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="py-8 text-center text-slate-400">No patient accounts registered.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="pt-4 border-t border-slate-100 mt-5">
            <Link to="/doctorDashboard/patients" className="text-xs font-bold text-secondary hover:text-secondary/80 flex items-center gap-1 justify-center">
              <span>View All Patients Directory</span>
              <Icon icon="solar:alt-arrow-right-linear" />
            </Link>
          </div>
        </div>

        {/* Recent Game Activity Stream */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex justify-between items-center mb-5">
              <div>
                <h4 className="text-base font-bold text-slate-800">Recent Patient Actions</h4>
                <p className="text-xs text-slate-400 mt-0.5">Therapy game play logs</p>
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full">Therapy feed</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-100 text-slate-400 text-xs font-semibold uppercase">
                    <th className="pb-3">Patient</th>
                    <th className="pb-3">Game</th>
                    <th className="pb-3 text-center">Score</th>
                    <th className="pb-3 text-right">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {stats?.recentGameRecords?.length > 0 ? (
                    stats.recentGameRecords.slice(0, 5).map((record) => (
                      <tr key={record._id} className="hover:bg-slate-50/50 transition">
                        <td className="py-3 font-semibold text-slate-800">{record.userName || "Unknown"}</td>
                        <td className="py-3">
                          <span className="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-medium">
                            {record.gameName}
                          </span>
                        </td>
                        <td className="py-3 text-center font-bold text-secondary">{record.score}</td>
                        <td className="py-3 text-right text-slate-400 text-xs">
                          {moment(record.createdAt || record.date).fromNow()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="py-8 text-center text-slate-400">No game sessions completed.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="pt-4 border-t border-slate-100 mt-5">
            <Link to="/doctorDashboard/statistics" className="text-xs font-bold text-[#01AEEF] hover:text-[#01AEEF]/80 flex items-center gap-1 justify-center">
              <span>View Practice Statistics Charts</span>
              <Icon icon="solar:alt-arrow-right-linear" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorDashboardIndex;
